import React, { useState } from "react";
import { useFooterData } from "./useFooterData";
import LogoSvg from "../../../../../prod/images/redesign/logo-type.svg?react";
import MailSvg from "../../../../../prod/images/redesign/mail.svg?react";
import "./Footer.scss";
import dompurify from "dompurify";
import { postSubscribe } from "../../../../api/newsletterApi";
import { SubscribeRequest } from "../../../../api/newsletterApi.types";
import { isValidEmail } from "../../utils/validateEmail";
import { sanitizeOrPassthrough } from "../../helpers/rteHelpers";
import { FooterData } from "../../../../api/footerApi.types";

export interface FooterProps {
  currentPageId: number;
  initialFooterData?: FooterData;
}

const Footer: React.FC<FooterProps> = (props) => {
  const [email, setEmail] = useState<string | null>(null);
  const [subResult, setSubResult] = useState<string | null>(null);
  const [subLoading, setSubLoading] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(evt.target.value);
    setEmailError(null);
  };

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
   
    if (!isValidEmail(email)) {
      setEmailError("Please insert a valid email address");
      return;
    }

    setSubLoading(true);
    const data: SubscribeRequest = {
      email: email,
      newsletterSubscribeBlockId: footerData?.newsletterSubscribeBlockId,
    };
    const response = await postSubscribe(data);

    setSubResult(response.message);
    setSubLoading(false);
  };

  const { footerData, loading } = useFooterData(props.currentPageId, props.initialFooterData);
  if (loading) {
    return null;
  }

  return (
    <div className="h-[860px] md:h-[640px] footer-redesign flex flex-col justify-between bg-primary text-white px-5 py-10 md:px-12 md:pt-20 md:pb-12">
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex flex-col order-last md:order-none w-full md:w-1/2 mt-10 md:mt-0">
          <div className="w-[200px] h-full">
            <LogoSvg />
          </div>
        </div>
        <div className="flex w-full md:w-1/2 flex-wrap mt-5 gap-y-10 md:gap-y-20 mb-20 md:mb-0">
          <div className="flex flex-col w-full md:w-1/2 leading-tight">
            <strong className="mb-5">Our business</strong>
            {footerData?.ourBusinessLinks?.map((link) => (
              <a className="hover:underline" key={link?.title} href={link?.url}>
                {link?.title}
              </a>
            ))}
          </div>
          <div className="flex flex-col w-1/2 leading-tight">
            <strong className="mb-5">About us</strong>
            {footerData?.aboutUsLinks?.map((link) => (
              <a className="hover:underline" key={link?.title} href={link?.url}>
                {link?.title}
              </a>
            ))}
          </div>
          <div className="flex flex-col w-1/2 leading-tight">
            <strong className="mb-5">Follow us</strong>
            {footerData?.footerSocials?.map((link) => (
              <a className="hover:underline" key={link?.title} href={link?.url}>
                {link?.title}
              </a>
            ))}
          </div>
          {!!footerData?.newsletterSubscribeBlockId && !subLoading && (
            <div className="flex flex-col w-full md:w-1/2">
              <span className="mb-5">Join our Newsletter</span>
              <form onSubmit={handleSubmit} className="flex flex-row">
                <div className="flex flex-row text-white bg-primaryBlue rounded grow mr-1 py-2.5">
                  <span className="flex items-center pr-2.5 pl-5">
                    <MailSvg />
                  </span>
                  <input
                    placeholder="E-mail"
                    className="flex w-full outline-none newsletter-input"
                    onChange={handleEmailChange}
                  />
                </div>
                <button
                  className="border-2 border-primaryBlue border-solid text-white hover:text-white rounded py-2.5 min-w-24 flex justify-center items-center"
                  type="submit"
                  disabled={!email || email.length === 0 || emailError !== null}
                >
                  Sign up
                </button>
              </form>
              {emailError && <span>{emailError}</span>}
              {subResult && <span>{subResult}</span>}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row w-full mt-10 md:mt-0">
        <div className="flex w-full md:w-1/2">
          <div
            className="flex flex-col md:flex-row md:gap-20 contact-info"
            dangerouslySetInnerHTML={{
              __html: sanitizeOrPassthrough(dompurify, footerData?.contactInformation),
            }}
          />
        </div>
        <div className="flex w-full md:w-1/2">
          <span>© 2024 Wilhelmsen. All rights reserved.</span>
        </div>
      </div>
    </div>
  );
};

export default Footer;